import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import ImageIcon from '~/assets/icons/image-format/pngjpg.svg?react';
import dribbbleImage from '~/assets/icons/social-icons/dribbble.png';
import facebookImage from '~/assets/icons/social-icons/facebook.png';
import githubImage from '~/assets/icons/social-icons/github.png';
import googleImage from '~/assets/icons/social-icons/google.png';
import instagramImage from '~/assets/icons/social-icons/instagram.png';
import linkedinImage from '~/assets/icons/social-icons/linkedin.png';
import spotifyImage from '~/assets/icons/social-icons/spotify.png';
import emptyImage from '~/assets/images/forbidden-circle.png';
import DesignStepBlockHeaderLayout from '~/components/QRCodeDesign/DesignStepBlockHeaderLayout';
import Accordion from '~/components/ui/Accordion/Accordion';
import ImageUploader from '~/components/ui/ImageUploader/ImageUploader';
import { Text } from '~/components/ui/text';
import { QrCodeLogoState } from '~/types/builder';

import { StyledLogoButton, StyledLogosContainer } from './styled';

const LOGOS = [
  { name: 'empty', img: emptyImage },
  { name: 'facebook', img: facebookImage },
  { name: 'google', img: googleImage },
  { name: 'linkedin', img: linkedinImage },
  { name: 'dribbble', img: dribbbleImage },
  { name: 'github', img: githubImage },
  { name: 'spotify', img: spotifyImage },
  { name: 'instagram', img: instagramImage },
];

type QRCodeLogoProps = {
  selectedLogo: QrCodeLogoState;
  onDeleteUploadedLogo?: () => void;
  defaultExpanded?: boolean;
  onSelectLogo: (logoState: QrCodeLogoState) => void;
};

export default function QRCodeLogo({
  selectedLogo,
  onSelectLogo,
  onDeleteUploadedLogo = () => {},
  defaultExpanded = false,
}: QRCodeLogoProps) {
  const { t } = useTranslation();

  const handleSelectLogo = async ({ img, name }) => {
    if (name === 'empty') {
      onSelectLogo({ name, file: null });
    } else {
      const imgResponse = await fetch(img);
      const blob = await imgResponse.blob();

      onSelectLogo({ name, file: new File([blob], `${name}.png`, blob) });
    }
  };

  const handleUploadLogo = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        onSelectLogo({ name: file.name, file, fileURL: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Accordion
      variant="medium"
      sx={{ maxWidth: '100%', width: '100%' }}
      defaultExpanded={defaultExpanded}
      summary={
        <DesignStepBlockHeaderLayout
          Icon={ImageIcon}
          title={t('builder:designStep.logo.title')}
          description={t('builder:designStep.logo.description')}
        />
      }
      details={
        <>
          <Text variant="inputSmall">{t('thirdStep:uploadLogo')}</Text>
          <ImageUploader
            onUploadImage={handleUploadLogo}
            image={selectedLogo.fileURL}
            onDeleteImage={onDeleteUploadedLogo}
          />
          <Text variant="h3" sx={{ marginTop: '16px', marginBottom: '12px' }}>
            {t('thirdStep:selectLogo')}
          </Text>
          <StyledLogosContainer>
            {LOGOS.map((logo) => {
              const isSelected = selectedLogo.name === logo.name;

              return (
                <StyledLogoButton key={logo.name} onClick={() => handleSelectLogo(logo)} $isSelected={isSelected}>
                  <img src={logo.img} alt={logo.name} width={32} height={32} />
                </StyledLogoButton>
              );
            })}
          </StyledLogosContainer>
        </>
      }
    />
  );
}
